import { ReactNode } from "react";
import { FieldName, SmartErrorAlertProps } from "../../components/SmartErrorAlert";
import ValidationAlertMessage from "../../components/ValidationAlertMessage";
import translate, { TranslationKey } from "../translate";
import english from '../../smartling/en.json';

type AlertHeading = {
    header?: string
    headerJSX?: never
} | {
    header?: never
    headerJSX?: ReactNode
}

export type ErrorContent = {
    body: ReactNode,
    analyticsErr: string
} & AlertHeading;

type RequiredInvalidFieldType = 'empty';
type OptionalInvalidFieldType = 'invalid' | 'alreadyUsed';
/**
 * Defines what invalid fields can be passed into an alert, as well as
 * invalid fields that can come back from API errors
 */
export type InvalidField<Field extends FieldName> = {
    name: Field
    type: RequiredInvalidFieldType | OptionalInvalidFieldType
}

/**
 * Used to define all invalid field messages to be used in an alert
 * Fields are the fields used for a given flow
 */
export type InvalidFieldMessages<Fields extends FieldName> = Record<Fields, {
    linkText: TranslationKey,
    messages: Record<RequiredInvalidFieldType, TranslationKey> & Partial<Record<OptionalInvalidFieldType, TranslationKey>>
}>

/** Helper type used to create lookup of ErrState to ErrorContent */
export type ErrorContentMap<ErrState extends string> = {
    [err in ErrState]: ErrorContent
}

/** Functions to be implemented by each flow using the <SmartErrorAlert /> */
export type ErrorContentFns<AlertProps extends SmartErrorAlertProps, Fields extends FieldName, ExtraData = any> = {

    getErrState: (apiResponse: AlertProps['apiResponse'], extraData?: ExtraData) => string | null
    /** Given an error state, return ErrorContent */
    getApiAlertContent: (errState: any) => ErrorContent
    /** Return invalid fields from given API response */
    getApiInvalidFields: (apiResponse: AlertProps['apiResponse']) => InvalidField<Fields>[]
    /**
     * Generates alert content for invalid fields, either from inline validation or from API response.
     * Should look at `props.invalidFields` to generate alert messages, falling back to
     * `getApiInvalidFields` if `props.invalidFields` has `length` of `0`
     * `props` also contains the necessary refs to give focus links to each relevant field
    */
    getInvalidFieldContent: (props: AlertProps) => ErrorContent
    /**
     * Tells conversion which failure methods to call. Will use `failValidation`
     * and `failAPI` (when `false`) or `failValidationIntermediate` and
     * `failAPIIntermediate` (when `true`)
    */
    isIntermediateStep: boolean
}

export class ErrorContentGenerator<
    AlertProps extends SmartErrorAlertProps,
    Fields extends FieldName,
    ErrState extends string,
    ExtraData = any // for components that need to pass in extra info to be used in calculating error state
> implements ErrorContentFns<AlertProps, Fields> {

    apiErrorContent: ErrorContentMap<ErrState>
    getErrState: (apiResponse: AlertProps['apiResponse'], extraData?: ExtraData) => ErrState | null
    getApiInvalidFields: ErrorContentFns<AlertProps, Fields>['getApiInvalidFields']
    invalidFieldHeader: string
    invalidFieldMessages: InvalidFieldMessages<Fields>
    isIntermediateStep: boolean
    getRefMap: (props: AlertProps) => RefMap<Fields>

    /** Handles all shared logic for generating error content */
    constructor(props: {
        apiErrorContent: ErrorContentMap<ErrState>
        invalidFieldMessages: InvalidFieldMessages<Fields>
        invalidFieldHeader: TranslationKey
        getErrState: (apiResponse: AlertProps['apiResponse'], extraData?: ExtraData) => ErrState | null
        getRefMap: (props: AlertProps) => RefMap<Fields>
        getApiInvalidFields: ErrorContentFns<AlertProps, Fields>['getApiInvalidFields']
        isIntermediateStep: boolean
    }) {
        this.getErrState = props.getErrState;
        this.apiErrorContent = props.apiErrorContent;
        this.getApiInvalidFields = props.getApiInvalidFields;
        this.invalidFieldHeader = translate(props.invalidFieldHeader);
        this.invalidFieldMessages = props.invalidFieldMessages;
        this.isIntermediateStep = props.isIntermediateStep;
        this.getRefMap = props.getRefMap;
    }

    getApiAlertContent(errState: ErrState) {
        return this.apiErrorContent[errState];
    }

    getInvalidFieldContent(props: AlertProps) {
        const { invalidFields } = props;
        let fields = invalidFields as InvalidField<Fields>[];
        if (!fields.length) {
            fields = this.getApiInvalidFields(props.apiResponse);
        }
        return buildInvalidFieldContent(this.invalidFieldHeader, this.getRefMap(props), fields, this.invalidFieldMessages, props.parentHeading ? props.parentHeading : '' );
    }
}


export type RefMap<Field extends FieldName> = Record<Field, HTMLElement | null>;

export interface InvalidFieldData {
    ref: HTMLElement | null
    linkText: TranslationKey
    message: TranslationKey,
    parentHeading: string
}

const getFieldMessages = (messages: InvalidFieldData[]) => {
    const formattedMessages = messages.map(x => {
        return <li key={x.message}>
            <ValidationAlertMessage
                linkText={translate(x.linkText)}
                description={translate(x.message)}
                fieldReference={x.ref}
                componentHeading={x.parentHeading ? x.parentHeading : ''}
            />
        </li>
    });

    return <ul className="ds-c-list">
        { formattedMessages }
    </ul>;
}

function buildInvalidFieldContent<Field extends FieldName>(header: string, refMap: RefMap<Field>, invalidFields: InvalidField<Field>[], messageContent: InvalidFieldMessages<Field>, parentHeading: string): ErrorContent {
    const messages: InvalidFieldData[] = invalidFields.map(x => {
        const { linkText, messages: message } = messageContent[x.name];
        return {
            ref: refMap[x.name],
            linkText,
            // fallbacks to "invalid" (optional but usually generic) and "empty" (required) so we always have some message
            message: message[x.type] || message.invalid || message.empty,
            parentHeading
        }
    })

    return {
        header,
        body: getFieldMessages(messages),
        analyticsErr: getFieldAnalyticsMessages(messages)
    }
}

const getFieldAnalyticsMessages = (messages: InvalidFieldData[]) => {
    return messages.map(x => english[x.message]).join(', ');
}